import React from 'react';
import {
  FileText, Gavel, Headphones, Trophy, Link as LinkIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { renderSettingText } from '../lib/textTemplate';
import { resolveApiAssetUrl } from '../lib/api';
import SponsorshipStrip from './SponsorshipStrip';
// Panelden yazılan CSS'in tutunacağı sabit kimlikler (bkz. lib/domHooks.js):
// kimlik şemaya gömülmez, ortak sabitten okunur.
import { SHELL_IDS } from '../lib/domHooks';

const iconMap = {
  'file-text': FileText,
  gavel: Gavel,
  headphones: Headphones,
  trophy: Trophy,
  link: LinkIcon,
};

const Footer = () => {
  const settings = useSettings();
  const menuGroups = settings.footer_menu_groups || [];
  const licenses = (settings.footer_licenses || []).filter((item) => item.image_url);
  // Sponsor şeridi iki temada da aynı ayar kaydından okunur; logosuz kayıt
  // çizilmez.
  const sponsors = (settings.footer_sponsors || []).filter((item) => item.image_url);

  return (
    <footer id={SHELL_IDS.footer} className="site-footer mt-16 border-t border-[var(--pusu-gold)]/25 bg-[var(--pusu-bg-deep)]">
      <div className="max-w-[1560px] mx-auto px-5 sm:px-8 py-12 md:py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-10">
        {menuGroups.map((group, groupIndex) => {
          const Icon = iconMap[group.icon] || LinkIcon;
          return (
            <section key={`${group.title}-${groupIndex}`}>
              <h2 className="flex items-center gap-2.5 text-[15px] font-black tracking-[0.08em] text-slate-100 mb-5">
                <Icon size={17} className="text-[#3cc487]" aria-hidden="true" />
                {renderSettingText(group.title, settings)}
              </h2>
              <ul className="space-y-3">
                {(group.links || []).map((item, itemIndex) => (
                  <li key={`${item.label}-${itemIndex}`}>
                    {(item.url || '').startsWith('/') && !item.new_tab ? (
                      <Link
                        to={item.url}
                        className="text-[15px] text-slate-400 hover:text-[var(--pusu-accent-light)] transition-colors"
                      >
                        {renderSettingText(item.label, settings)}
                      </Link>
                    ) : (
                      <a
                        href={item.url || '#'}
                        target={item.new_tab ? '_blank' : undefined}
                        rel={item.new_tab ? 'noreferrer' : undefined}
                        className="text-[15px] text-slate-400 hover:text-[var(--pusu-accent-light)] transition-colors"
                      >
                        {renderSettingText(item.label, settings)}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      {sponsors.length > 0 && (
        <div className="border-t border-white/[0.06]">
          <div className="max-w-5xl mx-auto px-5 py-8">
            {settings.footer_sponsor_title && (
              <h2 className="mb-5 text-center text-[12px] font-bold tracking-[0.08em] text-slate-400">
                {renderSettingText(settings.footer_sponsor_title, settings)}
              </h2>
            )}
            <div className="flex flex-wrap items-center justify-center gap-4">
              {sponsors.map((sponsor, index) => {
                const label = renderSettingText(sponsor.name || 'Sponsor', settings);
                const image = (
                  <img
                    src={resolveApiAssetUrl(sponsor.image_url)}
                    alt={label}
                    className="max-h-[42px] max-w-[140px] w-auto object-contain"
                    loading="lazy"
                  />
                );
                return sponsor.link ? (
                  <a
                    key={`${sponsor.name}-${index}`}
                    href={sponsor.link}
                    target={sponsor.new_tab ? '_blank' : undefined}
                    rel={sponsor.new_tab ? 'noreferrer' : undefined}
                    className="flex h-[52px] items-center justify-center"
                    aria-label={label}
                  >
                    {image}
                  </a>
                ) : (
                  <div key={`${sponsor.name}-${index}`} className="flex h-[52px] items-center justify-center">
                    {image}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {(licenses.length > 0 || settings.footer_license_text) && (
        <div className="border-t border-white/[0.06]">
          <div className="max-w-5xl mx-auto px-5 py-9">
            {licenses.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-4">
                {licenses.map((license, index) => {
                  const image = (
                    <img
                      src={resolveApiAssetUrl(license.image_url)}
                      alt={renderSettingText(license.name || 'Lisans', settings)}
                      className="max-h-[60px] max-w-[235px] w-auto object-contain"
                      loading="lazy"
                    />
                  );
                  return license.link ? (
                    <a
                      key={`${license.name}-${index}`}
                      href={license.link}
                      target={license.new_tab ? '_blank' : undefined}
                      rel={license.new_tab ? 'noreferrer' : undefined}
                      className="flex h-[62px] items-center justify-center"
                      aria-label={renderSettingText(
                        license.name || 'Lisans bağlantısı',
                        settings,
                      )}
                    >
                      {image}
                    </a>
                  ) : (
                    <div key={`${license.name}-${index}`} className="flex h-[62px] items-center justify-center">
                      {image}
                    </div>
                  );
                })}
              </div>
            )}
            {settings.footer_license_text && (
              <p className="mx-auto mt-4 max-w-4xl text-center text-[13px] leading-6 text-slate-500">
                {renderSettingText(settings.footer_license_text, settings)}
              </p>
            )}
          </div>
        </div>
      )}
      {/* Sponsorluk bandı footer'ın en altındadır ve beş kabukta da
          aynı bileşendir; kayıt yoksa hiçbir şey çizmez. */}
      <SponsorshipStrip />
    </footer>
  );
};

export default Footer;
